import {
  extractDurationFromNowMinus,
  getTimeRangeFromDurationString,
  isDurationString,
  isNowValue,
  type TimeRangeInputValue,
  useFeatureFlags,
} from '@chronosphereio/core';
import { useCallback, useMemo } from 'react';
import { encodeQueryParams, objectToSearchString, useQueryParams } from 'use-query-params';
import {
  type EndParamValue,
  getEndParamName,
  getStartParamName,
  getTimeRangeQueryParamsConfig,
  type StartParamValue,
} from './url-query-params-config';

/**
 * Gets the time range state from the query string. Always returns the latest value from the query string, which could
 * be `undefined` if there is no state in the query string. Tries to maintain object identity when `setTimeRangeInUrl`
 * is used.
 */
export function useTimeRangeQueryParamsState(startParamName: string, endParamName: string) {
  const { enableNewTimeRangeQueryParams } = useFeatureFlags();

  const [timeRangeParams, setTimeRangeParams] = useQueryParams(
    getTimeRangeQueryParamsConfig(startParamName, endParamName),
    // Default to `replaceIn` when updating the URL params, but allow consumers below to specify in `set` function
    { updateType: 'replaceIn' }
  );

  // The dynamic query param names make Typescript's generics confused here, so cast which should be safe
  const startParamValue: StartParamValue = timeRangeParams[startParamName];
  const endParamValue = timeRangeParams[endParamName] as EndParamValue;

  const setTimeRangeInUrl = useCallback(
    (next: TimeRangeInputValue, updateType: 'pushIn' | 'replaceIn') => {
      // Figure out what to actually encode / set, taking into account options / feature flag
      const queryParamValues = getQueryParamValuesToEncode(
        next,
        startParamName,
        endParamName,
        enableNewTimeRangeQueryParams
      );
      setTimeRangeParams(queryParamValues, updateType);
    },
    [enableNewTimeRangeQueryParams, endParamName, setTimeRangeParams, startParamName]
  );

  // Recalculated whenever the query string values change
  const timeRangeInUrl = useMemo(() => {
    return getTimeRangeFromParamValues(startParamValue, endParamValue);
  }, [startParamValue, endParamValue]);

  return {
    timeRangeInUrl,
    setTimeRangeInUrl,
  };
}

// Helper to turn the values in the query string back into a `TimeRangeInputValue`
function getTimeRangeFromParamValues(
  startParamValue: StartParamValue,
  endParamValue: EndParamValue
): TimeRangeInputValue | undefined {
  // The start param value can never be undefined, so if that's the case, we don't have a valid time range
  if (startParamValue === undefined) {
    return undefined;
  }

  // Look for the old relative time format
  if (typeof startParamValue === 'string' && isDurationString(startParamValue)) {
    // In the old format, the end param value _could_ be a Date, but the new format doesn't really support that, so
    // always default the end param to `now` regardless to avoid invalid values
    return getTimeRangeFromDurationString(startParamValue);
  }

  // In the new format, end param value should also never be undefined
  if (endParamValue === undefined) {
    return undefined;
  }

  return { start: startParamValue, end: endParamValue };
}

/**
 * Options when generating query string param values.
 */
export interface TimeRangeToQueryStringOptions {
  /**
   * Feature flag value.
   */
  enableNewTimeRangeQueryParams: boolean;
  /**
   * Any scope / prefix to add to the query param names. (This is the same as the option in the `TimeRangeProvider`.)
   */
  urlParamsScope?: string;
}

/**
 * Convert a `TimeRangeInputValue` to a query string (without the `?`). Useful when generting links to pages that use
 * time range controls.
 */
export function timeRangeToQueryString(timeRange: TimeRangeInputValue, options: TimeRangeToQueryStringOptions): string {
  const startParamName = getStartParamName(options.urlParamsScope);
  const endParamName = getEndParamName(options.urlParamsScope);
  const queryParamsConfig = getTimeRangeQueryParamsConfig(startParamName, endParamName);

  const queryParamValues = getQueryParamValuesToEncode(
    timeRange,
    startParamName,
    endParamName,
    options.enableNewTimeRangeQueryParams
  );

  const encoded = encodeQueryParams(queryParamsConfig, queryParamValues);
  return objectToSearchString(encoded);
}

/**
 * Sets a time range value in an existing instance of `URLSearchParams`. Modifies the `searchParams` provided.
 */
export function setTimeRangeInSearchParams(
  timeRange: TimeRangeInputValue,
  searchParams: URLSearchParams,
  options: TimeRangeToQueryStringOptions
) {
  const startParamName = getStartParamName(options.urlParamsScope);
  const endParamName = getEndParamName(options.urlParamsScope);
  const queryParamsConfig = getTimeRangeQueryParamsConfig(startParamName, endParamName);

  const queryParamValues = getQueryParamValuesToEncode(
    timeRange,
    startParamName,
    endParamName,
    options.enableNewTimeRangeQueryParams
  );

  const encoded = encodeQueryParams(queryParamsConfig, queryParamValues);
  for (const key in encoded) {
    const value = encoded[key];
    if (!value) {
      // Remove params that are null / undefined
      searchParams.delete(key);
      continue;
    }

    if (Array.isArray(value)) {
      // We shouldn't ever get an array, but we have to guard against it for Typescript
      continue;
    }

    // Otherwise just set the value
    searchParams.set(key, value);
  }
}

// Helper to get the query param values to encode in the query string, taking into account feature flag state and
// param names
function getQueryParamValuesToEncode(
  timeRange: TimeRangeInputValue,
  startParamName: string,
  endParamName: string,
  enableNewTimeRangeQueryParams: boolean
) {
  let startParamValue: StartParamValue = timeRange.start;
  let endParamValue: EndParamValue = timeRange.end;
  if (enableNewTimeRangeQueryParams === false) {
    // Convert 'now-x' to just the 'x' duration
    if (typeof timeRange.start === 'string') {
      const duration = extractDurationFromNowMinus(timeRange.start);
      if (duration !== null) {
        startParamValue = duration;
      }
    }

    // Convert 'now' to `undefined` so the end param is omitted like the old behavior
    if (typeof timeRange.end === 'string' && isNowValue(timeRange.end)) {
      endParamValue = undefined;
    }
  }

  return {
    [startParamName]: startParamValue,
    [endParamName]: endParamValue,
  };
}
